/* Mimi & Moto custom theme JS */

/**
 * Testimonials carousel.
 *
 * Reuses Dawn's SlideshowComponent wholesale for looping, autoplay and
 * pause-on-hover. The only change: SlideshowComponent assumes one slide is
 * visible at a time and marks every other slide aria-hidden / tabindex="-1".
 * This carousel shows several at once, so that would hide visible cards from
 * assistive tech. Overriding it to a no-op keeps every visible slide readable.
 */
if (typeof SlideshowComponent !== 'undefined') {
  class TestimonialSlider extends SlideshowComponent {
    setSlideVisibility() {}

    /**
     * Dawn only assigns `this.reducedMotion` inside its announcement-bar
     * branch, but setAutoPlay() reads `this.reducedMotion.matches` for every
     * slideshow that has no play/pause button - throwing before autoplay ever
     * starts. Provide it, then hand back to Dawn.
     */
    setAutoPlay() {
      this.reducedMotion = this.reducedMotion || window.matchMedia('(prefers-reduced-motion: reduce)');
      super.setAutoPlay();
    }

    /**
     * Pause on hover/focus. Dawn's version (for sliders without a play/pause
     * button) only pauses when the event is inside `this.announcementBarSlider`,
     * which is null outside the announcement bar - so every hover or focus
     * threw and autoplay never paused.
     */
    focusInHandling() {
      this.pause();
    }

    /**
     * Looping, driven by real scroll extents rather than Dawn's page counter.
     *
     * Dawn works out "last slide" from currentPage/totalPages, but its
     * slidesPerPage math ignores the flex `gap`: with 3 of 4 cards visible it
     * reports 2 per page and 3 total pages, so the counter never reaches the
     * end and the carousel deadlocks at max scroll. Scroll position can't
     * disagree with itself, so wrap on that instead - and it stays correct at
     * any slides-per-view the merchant picks.
     */
    get maxScroll() {
      return this.slider.scrollWidth - this.slider.clientWidth;
    }

    // Sub-pixel widths mean scrollLeft rarely lands exactly on the boundary.
    atScrollEnd() {
      return this.slider.scrollLeft >= this.maxScroll - 2;
    }

    atScrollStart() {
      return this.slider.scrollLeft <= 2;
    }

    advance(isNext) {
      let position;

      if (isNext) {
        position = this.atScrollEnd() ? 0 : Math.min(this.slider.scrollLeft + this.sliderItemOffset, this.maxScroll);
      } else {
        position = this.atScrollStart() ? this.maxScroll : Math.max(this.slider.scrollLeft - this.sliderItemOffset, 0);
      }

      this.setSlidePosition(position);
    }

    autoRotateSlides() {
      this.advance(true);
    }

    onButtonClick(event) {
      event.preventDefault();
      this.advance(event.currentTarget.name === 'next');
    }
  }

  if (!customElements.get('testimonial-slider')) {
    customElements.define('testimonial-slider', TestimonialSlider);
  }
}

/**
 * Vertical thumbnail carousel for the product media gallery (desktop only).
 *
 * Figma puts the thumbnails in a column beside the main image, six visible at
 * a time with arrows to page through the rest. Dawn's SliderComponent is
 * horizontal-only - it measures with offsetLeft/clientWidth and scrolls with
 * scrollLeft - and its update() would read a column as "everything visible"
 * and permanently disable the next arrow. So rather than fight it, Dawn's
 * arrow buttons are cloned (which drops its listeners) and driven vertically
 * here. Below 990px this does nothing and Dawn's stock mobile slider is left
 * completely alone, matching the CSS in custom.css.
 */
{
  const DESKTOP_QUERY = '(min-width: 990px)';
  const VISIBLE_THUMBS = 6;

  class VerticalThumbnails {
    constructor(sliderComponent) {
      this.sliderComponent = sliderComponent;
      this.list = sliderComponent.querySelector('.thumbnail-list');
      this.enabled = false;
      if (!this.list) return;

      // Cloning strips Dawn's click handlers while keeping its markup, icons
      // and styling hooks.
      this.buttons = Array.from(sliderComponent.querySelectorAll('.slider-button')).map((button) => {
        const clone = button.cloneNode(true);
        button.replaceWith(clone);
        clone.addEventListener('click', this.onButtonClick.bind(this));
        return clone;
      });

      this.prevButton = this.buttons.find((button) => button.name === 'previous');
      this.nextButton = this.buttons.find((button) => button.name === 'next');

      this.onScroll = this.updateButtons.bind(this);
      this.list.addEventListener('scroll', this.onScroll);

      this.mediaQuery = window.matchMedia(DESKTOP_QUERY);
      this.mediaQuery.addEventListener('change', () => this.refresh());
      this.refresh();
    }

    get items() {
      return Array.from(this.list.querySelectorAll('.thumbnail-list__item'));
    }

    // One "page" is however far six thumbnails span, derived from the real
    // rendered offsets so it follows whatever size/gap the CSS uses.
    get pageDistance() {
      const items = this.items;
      if (items.length < 2) return this.list.clientHeight;
      const step = items[1].offsetTop - items[0].offsetTop;
      return step * VISIBLE_THUMBS;
    }

    refresh() {
      this.enabled = this.mediaQuery.matches;
      this.sliderComponent.classList.toggle('thumbnail-slider--vertical', this.enabled);
      if (this.enabled) this.updateButtons();
    }

    updateButtons() {
      if (!this.enabled || !this.prevButton || !this.nextButton) return;

      const { scrollTop, scrollHeight, clientHeight } = this.list;
      const atStart = scrollTop <= 1;
      const atEnd = scrollTop + clientHeight >= scrollHeight - 1;

      this.prevButton.toggleAttribute('disabled', atStart);
      this.nextButton.toggleAttribute('disabled', atEnd);
      // Nothing to page through - hide both rather than show two dead arrows.
      this.sliderComponent.classList.toggle('thumbnail-slider--no-paging', atStart && atEnd);
    }

    onButtonClick(event) {
      if (!this.enabled) return;
      event.preventDefault();

      const direction = event.currentTarget.name === 'next' ? 1 : -1;
      this.list.scrollBy({ top: direction * this.pageDistance, behavior: 'smooth' });
    }
  }

  const initVerticalThumbnails = () => {
    document.querySelectorAll('.product__media-wrapper .thumbnail-slider').forEach((sliderComponent) => {
      if (sliderComponent.dataset.verticalThumbs === 'true') return;
      sliderComponent.dataset.verticalThumbs = 'true';
      new VerticalThumbnails(sliderComponent);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVerticalThumbnails);
  } else {
    initVerticalThumbnails();
  }
}

/**
 * Gallery lightbox (`sections/gallery.liquid`).
 *
 * Extends Dawn's ModalDialog (global.js) instead of building a modal from
 * scratch - that base class already handles opening/closing, the focus trap,
 * ESC-to-close and click-outside-to-close (via its .media-modal branch, which
 * this element carries). Only image-navigation state is added here.
 */
if (typeof ModalDialog !== 'undefined' && !customElements.get('gallery-lightbox')) {
  customElements.define(
    'gallery-lightbox',
    class GalleryLightbox extends ModalDialog {
      constructor() {
        super();
        this.imageEl = this.querySelector('.gallery-lightbox__image');
        this.spinner = this.querySelector('.gallery-lightbox__spinner');
        this.items = [];
        this.currentIndex = 0;
        this.loadToken = 0;

        const prevButton = this.querySelector('[name="previous"]');
        const nextButton = this.querySelector('[name="next"]');

        // ModalDialog (the base class - global.js) closes any .media-modal on
        // pointerup unless the click target is inside deferred-media/
        // product-model - a check written for the product image modal, which
        // has no other controls layered over the image. Without stopping it
        // here, clicking either arrow both navigates AND closes the lightbox,
        // since pointerup bubbles up and fires before click.
        prevButton.addEventListener('pointerup', (event) => event.stopPropagation());
        nextButton.addEventListener('pointerup', (event) => event.stopPropagation());

        prevButton.addEventListener('click', () => this.showRelative(-1));
        nextButton.addEventListener('click', () => this.showRelative(1));
        this.addEventListener('keyup', (event) => {
          if (event.code === 'ArrowLeft') this.showRelative(-1);
          if (event.code === 'ArrowRight') this.showRelative(1);
        });
      }

      open(items, index, opener) {
        this.items = items;
        this.currentIndex = index;
        this.renderCurrent();
        this.show(opener);
      }

      showRelative(direction) {
        const total = this.items.length;
        this.currentIndex = (this.currentIndex + direction + total) % total;
        this.renderCurrent();
      }

      renderCurrent() {
        const item = this.items[this.currentIndex];
        if (!item) return;

        // Preloading (below) covers ordinary next/prev clicking, but these
        // are full photos, not small illustrations - someone clicking
        // through faster than the network can keep up will still hit a
        // genuine wait sometimes. The token guards against a stale, slow
        // earlier load finishing after the shopper has already moved past
        // it and wrongly hiding the spinner for the image actually showing.
        const token = ++this.loadToken;
        this.imageEl.classList.add('is-loading');
        // Delayed so the far more common already-preloaded case doesn't
        // flash a spinner for one frame.
        const spinnerTimer = setTimeout(() => {
          if (token === this.loadToken) this.spinner.classList.remove('hidden');
        }, 200);
        this.imageEl.addEventListener(
          'load',
          () => {
            clearTimeout(spinnerTimer);
            if (token === this.loadToken) {
              this.imageEl.classList.remove('is-loading');
              this.spinner.classList.add('hidden');
            }
          },
          { once: true }
        );

        this.imageEl.src = item.src;
        this.imageEl.width = item.width;
        this.imageEl.height = item.height;
        this.imageEl.alt = item.alt;

        // Fetch a couple of images out in each direction now, in the
        // background, so by the time the shopper clicks an arrow the image
        // is usually already in the browser's cache instead of starting its
        // (up to 1800px) download only at that click - that gap was the
        // reported delay. Two deep, not the whole gallery: a page can hold
        // dozens of images across its blocks, and most will never be opened;
        // preloading everything would just slow the ones that do get viewed.
        this.preload(this.currentIndex + 1);
        this.preload(this.currentIndex - 1);
        this.preload(this.currentIndex + 2);
        this.preload(this.currentIndex - 2);
      }

      preload(index) {
        const total = this.items.length;
        const item = this.items[(index + total) % total];
        if (!item || item.preloaded) return;
        item.preloaded = true;
        new Image().src = item.src;
      }
    }
  );

  const initGalleries = () => {
    document.querySelectorAll('.gallery__grid[data-lightbox]').forEach((grid) => {
      const lightbox = document.getElementById(grid.dataset.lightbox);
      if (!lightbox) return;

      const images = Array.from(grid.querySelectorAll('.gallery__image'));
      const items = images.map((img) => ({
        src: img.dataset.fullSrc,
        width: img.dataset.fullWidth,
        height: img.dataset.fullHeight,
        alt: img.alt,
      }));

      images.forEach((img, index) => {
        img.closest('.gallery__item-button')?.addEventListener('click', (event) => {
          lightbox.open(items, index, event.currentTarget);
        });
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGalleries);
  } else {
    initGalleries();
  }
}

/* Product description read more / read less.
   Button stays hidden unless the text actually overflows the collapsed height. */
if (!customElements.get('read-more-text')) {
  customElements.define(
    'read-more-text',
    class ReadMoreText extends HTMLElement {
      connectedCallback() {
        this.body = this.querySelector('.product__description-body');
        this.button = this.querySelector('.product__description-more');
        this.label = this.querySelector('.product__description-more-text');

        this.button.addEventListener('click', () => this.toggle());
        this.checkOverflow();

        /* Keep the observer on `this` - an unreferenced ResizeObserver can be
           collected before it ever fires. Webfonts landing after first paint
           reflow the text, so re-check once they're ready too. */
        this.observer = new ResizeObserver(() => this.checkOverflow());
        this.observer.observe(this.body);
        document.fonts?.ready.then(() => this.checkOverflow());
      }

      checkOverflow() {
        if (this.classList.contains('is-expanded')) return;
        this.button.hidden = this.body.scrollHeight <= this.body.clientHeight + 1;
      }

      toggle() {
        const expanded = this.classList.toggle('is-expanded');
        this.button.setAttribute('aria-expanded', expanded);
        this.label.textContent = expanded ? this.label.dataset.less : this.label.dataset.more;
      }
    }
  );
}

/* Testimonials: our arrows drive a Judge.me Cards carousel app block through
   Judge.me's own global navigation. Delegated so it survives the theme editor
   re-rendering the section. */
document.addEventListener('click', (event) => {
  const button = event.target.closest('.testimonials--app .slider-button');
  if (!button) return;
  const carousel = button.closest('.testimonials--app').querySelector('[data-jdgm-block-id]');
  const navigate = button.name === 'next' ? window.jdgmNextCard : window.jdgmPreviousCard;
  if (carousel && navigate) navigate(carousel.dataset.jdgmBlockId);
});

/* Judge.me only positions the Cards carousel track when navigating, so a
   width change (window resize, device rotation, or layout settling after its
   init on mobile) strands it between cards until the next click. Whenever the
   carousel resizes, snap back onto a card edge. The card index is worked out
   with the step from before the resize, so a large shrink doesn't round onto
   the neighbouring card and desync from Judge.me's own index. */
{
  const cardsResnapObservers = [];

  const initCardsResnap = () => {
    document.querySelectorAll('.testimonials--app .jdgm-cards-carousel').forEach((carousel) => {
      let lastStep = 0;

      const observer = new ResizeObserver(() => {
        const track = carousel.querySelector('.jdgm-videos-container');
        const cards = track ? track.querySelectorAll('.jdgm-media-card') : [];
        if (cards.length < 2) return;

        const step = cards[1].offsetLeft - cards[0].offsetLeft;
        const offset = parseFloat((track.style.transform.match(/-?[\d.]+/) || [0])[0]);

        const basis = lastStep || step;
        const card = offset && basis ? cards[Math.round(-offset / basis)] : null;

        if (card && Math.abs(card.offsetLeft + offset) > 1) {
          track.style.transition = 'none';
          track.style.transform = `translateX(${-card.offsetLeft}px)`;
          void track.offsetWidth;
          track.style.transition = '';
        }

        if (step) lastStep = step;
      });

      observer.observe(carousel);
      cardsResnapObservers.push(observer);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCardsResnap);
  } else {
    initCardsResnap();
  }
}

/* Testimonials "Write a review" button (testimonials.liquid). Opens the form of
   Judge.me's Review Widget - in the app section under the testimonials, with
   its review list hidden by custom.css - and scrolls to it. The button renders
   hidden and only appears once Judge.me has set the widget up: before that its
   link is a bare href="#" that would just jump to the top of the page. */
{
  const waitFor = (check, timeout = 3000) =>
    new Promise((resolve) => {
      const start = performance.now();
      const tick = () => {
        const value = check();
        if (value || performance.now() - start > timeout) return resolve(value);
        requestAnimationFrame(tick);
      };
      tick();
    });

  const initWriteReview = () => {
    const buttons = document.querySelectorAll('[data-write-review]');
    const widget = document.getElementById('judgeme_product_reviews');
    if (!buttons.length || !widget) return;

    const link = () => widget.querySelector('.jdgm-write-rev-link');
    const isOpen = () => link()?.getAttribute('aria-expanded') === 'true';

    const reveal = () => {
      if (!widget.classList.contains('jdgm--done-setup-widget') || !link()) return false;
      buttons.forEach((button) => (button.hidden = false));
      return true;
    };

    if (!reveal()) {
      const observer = new MutationObserver(() => {
        if (reveal()) observer.disconnect();
      });
      observer.observe(widget, { attributes: true, childList: true, subtree: true });
    }

    buttons.forEach((button) =>
      button.addEventListener('click', async () => {
        if (!isOpen()) {
          link().click();
          // For products with no reviews yet Judge.me builds the form lazily:
          // that first click only creates it, so click again once it exists.
          if (!(await waitFor(isOpen, 400))) {
            await waitFor(() => widget.querySelector('.jdgm-form-wrapper'));
            if (!isOpen()) link().click();
          }
        }
        const form = await waitFor(() => isOpen() && widget.querySelector('.jdgm-form-wrapper'));
        if (form) form.scrollIntoView({ behavior: 'smooth', block: 'start' });
      })
    );
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWriteReview);
  } else {
    initWriteReview();
  }
}

/* Judge.me review titles on the testimonials Cards carousel. The carousel has
   no option to show titles, but Judge.me keeps the reviews it fetched (titles
   included) in window.jdgmCarouselMode, and every card - looping clones too -
   carries its review uuid in data-review-id. Titles can contain HTML entities,
   so they're decoded with DOMParser (inert - nothing executes) and inserted as
   plain text. */
{
  const reviewTitleObservers = [];

  const decodeTitle = (value) =>
    new DOMParser().parseFromString(String(value || ''), 'text/html').documentElement.textContent.trim();

  const addReviewTitles = (carousel) => {
    const mode = window.jdgmCarouselMode && window.jdgmCarouselMode[carousel.dataset.jdgmBlockId];
    const reviews = mode && Array.isArray(mode.reviews) ? mode.reviews : [];
    if (!reviews.length) return;

    const titles = new Map(reviews.map((review) => [review.uuid, decodeTitle(review.title)]));

    carousel.querySelectorAll('.jdgm-media-card').forEach((card) => {
      const title = titles.get(card.dataset.reviewId);
      if (!title || card.querySelector('.testimonials__review-title')) return;

      const heading = document.createElement('p');
      heading.className = 'testimonials__review-title';
      heading.textContent = title;
      card.insertBefore(heading, card.querySelector('.jdgm-text'));
    });
  };

  const initReviewTitles = () => {
    document.querySelectorAll('.testimonials--app .jdgm-cards-carousel').forEach((carousel) => {
      addReviewTitles(carousel);
      // Cards (and their clones) are built after load; add titles as they land.
      const observer = new MutationObserver(() => addReviewTitles(carousel));
      observer.observe(carousel, { childList: true, subtree: true });
      reviewTitleObservers.push(observer);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReviewTitles);
  } else {
    initReviewTitles();
  }
}
